// src/components/vault/ui/VaultRequirementContent.tsx
import {
  AlertCircle,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Loader2,
  SendHorizonal,
  X,
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { cn } from "../../../lib/utils";
import { Requirement } from "../../../redux/interfaces/folder.types";
import { Button } from "../../ui/button";
import { useBreakpoint } from "../../../hooks/useIsMobile";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../ui/alert-dialog";

type RequirementSubmitMode = "review" | "continue";

interface VaultRequirementContentProps {
  requirement: Requirement | null;
  children: React.ReactNode;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  hideNavigation?: boolean;
  isSubmittable?: boolean;
  submitMode?: RequirementSubmitMode;
  /** Whether a submission is currently in progress */
  isSubmitting?: boolean;
  /** Disable forward navigation while an asynchronous prerequisite is incomplete. */
  isForwardDisabled?: boolean;
  /** Display name of the next tab for cross-tab navigation (undefined if no next tab) */
  nextTabName?: string;
  /** Handler to navigate to the next tab */
  onNextTab?: () => void;
  /** Section banner info shown on cross-tab navigation */
  sectionBanner?: {
    name: string;
    description: string;
    stepCount: number;
  } | null;
  /** Dismiss the section banner */
  onDismissBanner?: () => void;
}

export function VaultRequirementContent({
  requirement,
  children,
  onPrevious,
  onNext,
  hasPrevious = false,
  hasNext = false,
  hideNavigation = false,
  isSubmittable = false,
  submitMode = "review",
  isSubmitting = false,
  isForwardDisabled = false,
  nextTabName,
  onNextTab,
  sectionBanner,
  onDismissBanner,
}: VaultRequirementContentProps) {
  const { isMobile, isDesktop } = useBreakpoint();
  if (!requirement) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-500">
        <p>Selecciona un requisito para comenzar</p>
      </div>
    );
  }

  // Cross-tab navigation: show when on last requirement, not submittable, and there's a next tab
  const showCrossTabButton = !hasNext && !isSubmittable && !!nextTabName;
  const isReviewSubmit = isSubmittable && submitMode === "review";
  const isContinueSubmit = isSubmittable && submitMode === "continue";

  // Whether this is a submittable last requirement with a next tab available
  const isSubmitAndCrossTab = isReviewSubmit && !hasNext && !!nextTabName;

  // Determine button text and visibility
  const showForwardButton = hasNext || isSubmittable;
  const forwardButtonLabel = isReviewSubmit
    ? isSubmitAndCrossTab
      ? `Enviar y continuar a ${nextTabName}`
      : hasNext
        ? "Enviar a revisión y continuar"
        : "Enviar a revisión"
    : isContinueSubmit
      ? "Continuar"
    : "Siguiente";

  // Forward button content (within-tab navigation or submit)
  const forwardButton = (
    <Button
      onClick={isReviewSubmit ? undefined : onNext}
      disabled={isSubmitting || isForwardDisabled}
      className={cn(
        "gap-2 bg-slate-900 hover:bg-slate-800 text-white",
        !showForwardButton && "invisible",
      )}
    >
      {isSubmitting ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : isReviewSubmit ? (
        <SendHorizonal className="h-4 w-4" />
      ) : null}
      {forwardButtonLabel}
      {isSubmitAndCrossTab && <ArrowRight className="w-4 h-4" />}
      {((!isSubmittable && hasNext) || isContinueSubmit) && (
        <ChevronRight className="w-4 h-4" />
      )}
    </Button>
  );

  // Cross-tab navigation button
  const crossTabButton = showCrossTabButton ? (
    <Button
      onClick={onNextTab}
      disabled={isSubmitting || isForwardDisabled}
      className="gap-2 bg-slate-900 hover:bg-slate-800 text-white"
    >
      Continuar a {nextTabName}
      <ArrowRight className="w-4 h-4" />
    </Button>
  ) : null;

  return (
    <div className={cn(isMobile ? "space-y-4" : "space-y-6")}>
      {/* Section banner — shown on cross-tab navigation, mobile only */}
      <AnimatePresence>
        {sectionBanner && isMobile && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="overflow-hidden"
            role="status"
            aria-live="polite"
          >
            <div className="flex items-start justify-between gap-3 rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 mb-1">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900">
                  {sectionBanner.name}
                </p>
                {sectionBanner.description && (
                  <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                    {sectionBanner.description}
                  </p>
                )}
                <p className="text-xs text-slate-400 mt-1">
                  {sectionBanner.stepCount} pasos en esta sección
                </p>
              </div>
              <button
                onClick={onDismissBanner}
                className="flex-shrink-0 p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                aria-label="Cerrar"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="space-y-2">
        <h1
          className={cn(
            "font-bold text-slate-900",
            isMobile ? "text-xl" : "text-2xl",
          )}
        >
          {requirement.name}
        </h1>
      </div>

      {/* Content — pending_review is no longer a blocking state.
          All requirement types render their normal children and let the user advance. */}
      <div className={cn(isMobile ? "min-h-[200px]" : "min-h-[300px]")}>
        {children}
      </div>

      {/* Navigation footer — sticky so it's always visible without scrolling */}
      {!hideNavigation && isDesktop && (
        <div className="sticky bottom-0 flex items-center justify-between pt-4 pb-2 mt-6 border-t border-slate-100 bg-white">
          <Button
            variant="ghost"
            onClick={onPrevious}
            disabled={!hasPrevious || isSubmitting}
            className={cn(
              "gap-2 text-slate-600 hover:text-slate-900",
              !hasPrevious && "invisible",
            )}
          >
            <ChevronLeft className="w-4 h-4" />
            Anterior
          </Button>

          {isReviewSubmit ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>{forwardButton}</AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirmar envío</AlertDialogTitle>
                  <AlertDialogDescription>
                    ¿Estás seguro de enviar el requisito a revisión? Una vez
                    enviado, no podrás modificar los datos hasta que sea
                    revisado.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={isSubmitting}>
                    Cancelar
                  </AlertDialogCancel>
                  <AlertDialogAction onClick={onNext} disabled={isSubmitting}>
                    Confirmar
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : showCrossTabButton ? (
            crossTabButton
          ) : (
            forwardButton
          )}
        </div>
      )}

      {/* Mobile navigation is handled by MobileNavBar (fixed bottom bar) */}
    </div>
  );
}

// Wrapper for requirement form/document content
interface VaultRequirementCardProps {
  children: React.ReactNode;
  className?: string;
}

export function VaultRequirementCard({
  children,
  className,
}: VaultRequirementCardProps) {
  return (
    <div
      className={cn(
        "bg-white rounded-xl border border-slate-200 p-6 shadow-sm",
        className,
      )}
    >
      {children}
    </div>
  );
}

// Empty state for when no requirement is selected
export function VaultEmptyState() {
  return (
    <div className="flex flex-col items-center justify-center h-64 text-center">
      <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
        <AlertCircle className="w-8 h-8 text-slate-400" />
      </div>
      <h3 className="text-lg font-medium text-slate-900 mb-2">
        Selecciona un requisito
      </h3>
      <p className="text-sm text-slate-500 max-w-sm">
        Elige un requisito del menu lateral para comenzar a completar tu
        documentacion.
      </p>
    </div>
  );
}
